import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	Radio,
	RadioGroup,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SeriesReportDownloadButton from '@/pages/report-manage/report-list/components/SeriesReportDownloadButton';
import { FormType } from '@/api/enum/formType.enum';
import { useCallback, useEffect, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import RichTextDisplay from '@/common/components/form-view/RichTextDisplay';
import {
	useDeleteMeditationMutation,
	useGetMeditationDetailQuery,
	useGetMeditationSingleListQuery,
	useSetMeditationStatusMutation,
} from '@/api/exercise/meditationApiSlice';
import MeditationInfoForm from './components/MeditationInfoForm';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import { ExerciseColor, OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import AppImageView from '@/common/components/form-view/AppImageView';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	MeditationSinglePageDTO,
	MeditationSinglePageFilter,
} from '@/api/DTO/exercise/meditation.interface';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { GridColDef } from '@mui/x-data-grid';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import SetSingleStatusDialog from './components/SetSingleStatusDialog';
import SetSingleOrderDialog from './components/SetSingleOrderDialog';
import SetSingleNewDialog from './components/SetSingleNewDialog';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function MeditationDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteMeditation] = useDeleteMeditationMutation();
	const { meditationId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');
	const [setMeditationStatus] = useSetMeditationStatusMutation();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const statusList = [
		{ id: OnoffType.off, name: t('meditation.off') },
		{ id: OnoffType.on, name: t('meditation.on') },
	];

	const colorList = [
		{ id: ExerciseColor.AQUA, name: t('meditation.AQUA') },
		{ id: ExerciseColor.ORANGE, name: t('meditation.ORANGE') },
		{ id: ExerciseColor.CORAL, name: t('meditation.CORAL') },
		{ id: ExerciseColor.PINK, name: t('meditation.PINK') },
		{ id: ExerciseColor.PURPLE, name: t('meditation.PURPLE') },
		{ id: ExerciseColor.LAVENDER, name: t('meditation.LAVENDER') },
		{ id: ExerciseColor.BLUE, name: t('meditation.BLUE') },
		// { id: ExerciseColor.CX, name: t('meditation.CX') },
		// { id: ExerciseColor.MTR, name: t('meditation.MTR') },
	];

	const exerciseTypeList = [
		{ id: 1, name: t('meditation.audio') },
		{ id: 2, name: t('meditation.video') },
	];
	const canList = [
		{ id: true, name: t('meditation.can') },
		{ id: false, name: t('meditation.cannot') },
	];
	const yesOrNoList = [
		{ id: true, name: t('meditation.yes') },
		{ id: false, name: t('meditation.no') },
	];

	const { data: details } = useGetMeditationDetailQuery(meditationId || '', {
		refetchOnMountOrArgChange: true,
	});
	const defaultPropertyFilter: MeditationSinglePageFilter = {
		pagination: initialPagination,
		sortings: [],
		id: meditationId || '',
	};

	const defaultValues: DefaultValues<MeditationSinglePageFilter> =
		defaultPropertyFilter;
	const methods = useForm<MeditationSinglePageFilter>({
		defaultValues,
	});

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as MeditationSinglePageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformedSearchParams: MeditationSinglePageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'published',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMeditationSingleListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: MeditationSinglePageDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	); // watch fields change

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.status == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('meditation.deleteSeries')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('meditation.isDeleteSeries')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteMeditation(meditationId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteMeditationSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate('/exercise-manage/meditation-list');
					}
				}}
			/>
		));
	};

	const handleSetStatus = async (type: number) => {
		const response = await setMeditationStatus({
			seriesPublishDTOList: [
				{ seriesId: meditationId || '', published: type == 1 ? true : false },
			],
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateExerciseStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'singleId',
			headerName: t('meditation.exerciseTag'),
			flex: 25,
			renderCell: (params) => {
				const arr = (params.row.tags || []).map(
					(item: { tagNameZh: any }) => item.tagNameZh
				);
				return arr.join(',');
			},
		},
		{
			field: 'titleZh',
			headerName: t('meditation.exerciseTitleZh'),
			flex: 25,
		},
		{
			field: 'mediaType',
			headerName: t('meditation.exerciseType'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.mediaType, exerciseTypeList)}</>
			),
		},
		{
			field: 'canCountTime',
			headerName: t('meditation.isNumberTotal'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCountTime, canList)}</>
			),
		},
		{
			field: 'canCollectPoints',
			headerName: t('meditation.isGetAward'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCollectPoints, canList)}</>
			),
		},
		{
			field: 'rewardPoints',
			headerName: t('achievementList.rewardPoints'),
			flex: 15,
			renderCell: (params: any) => params.row.rewardPoints || '-',
		},
		{
			field: 'rewardDesc',
			headerName: t('achievementList.rewardProp'),
			flex: 25,
			renderCell: (params: any) => params.row.rewardDesc || '-',
		},
		{
			field: 'hasNew',
			headerName: t('meditation.isNewExercise'),
			flex: 15,
			renderCell: (params) => <>{showLabel(params.row.hasNew, yesOrNoList)}</>,
		},
		{
			field: 'status',
			headerName: t('meditation.exerciseStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<Chip
					color={params.row.status == OnoffType.on ? 'chipPrimary' : 'default'}
					label={showLabel(params.row.status, statusList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('meditation.lastUpdateTime'),
			flex: 20,
		},
		{
			field: 'createdAt',
			headerName: t('meditation.createTime'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/exercise-manage/meditation-list/info/${meditationId}/detail/${params.row.singleId}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.meditationDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<SeriesReportDownloadButton
							seriesId={meditationId}
							seriesTitle={details?.data.titleZh}
						/>
						{details?.data.status == OnoffType.on && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(0)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('meditation.handleOff')}
							</Button>
						)}
						{details?.data.status == OnoffType.off && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(1)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('meditation.handleOn')}
							</Button>
						)}
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.seriesStatus')}
									valueElement={
										<Chip
											color={
												details?.data.status == OnoffType.on
													? 'chipPrimary'
													: 'default'
											}
											label={showLabel(details?.data.status, statusList)}
										/>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.title')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.subTitle')}
													value={
														currentTab == 'zh'
															? details?.data.subtitleZh
															: currentTab == 'en'
															? details?.data.subtitleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.color')}
													value={
														showLabel(details?.data.color, colorList) || '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.isHiddenPracticeAndSearch')}
													value={
														details?.data.hiddenSearch
															? t('common.yes')
															: t('common.no')
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('meditation.icon')}
													imageElement={
														details?.data.icon && (
															<img
																src={details?.data.icon.url}
																width={110}
																height={132}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('meditation.coverImage')}
													imageElement={
														details?.data.coverImage && (
															<img
																src={details?.data.coverImage.url}
																width={110}
																height={132}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
										</Grid>
										<Divider />
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												{details?.data && (
													<FormControlLabel
														control={
															<Checkbox
																disabled
																checked={details?.data.hasContent}
															/>
														}
														label={t('meditation.hasContent')}
													/>
												)}
											</Grid>
											{details?.data.hasContent && (
												<>
													{currentTab == 'zh' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('meditation.content')}
																editorStateString={
																	details?.data.contentZh || '-'
																}
															/>
														</Grid>
													)}
													{currentTab == 'en' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('meditation.content')}
																editorStateString={
																	details?.data.contentEn || '-'
																}
															/>
														</Grid>
													)}
													<Grid item xs={6}>
														{details?.data && (
															<FormControl>
																<RadioGroup
																	row
																	name='mediaType'
																	value={details?.data.mediaType}
																>
																	<FormControlLabel
																		disabled
																		value={3}
																		control={<Radio />}
																		label={t('meditation.image')}
																	/>
																	<FormControlLabel
																		disabled
																		value={2}
																		control={<Radio />}
																		label={t('meditation.video')}
																	/>
																</RadioGroup>
															</FormControl>
														)}
													</Grid>
													{details?.data.mediaType == 3 && (
														<Grid item xs={6}>
															<AppImageView
																label={t('meditation.mediaImage')}
																imageElement={
																	details?.data.media && (
																		<img
																			src={details?.data.media.url}
																			width={110}
																			style={{
																				objectFit: 'contain',
																			}}
																		/>
																	)
																}
															/>
														</Grid>
													)}
													{details?.data.mediaType == 2 && (
														<>
															<Grid item xs={6}>
																<Grid item xs={12}>
																	<AppFieldView
																		label={t('meditation.mediaVideo')}
																		value={details?.data.media?.name || '-'}
																		link={details?.data.media?.url}
																	/>
																</Grid>
															</Grid>
															<Grid item xs={6}>
																<AppImageView
																	label={t('day21.previewImage')}
																	imageElement={
																		details?.data
																			.introductionMediaVideoCover && (
																			<img
																				src={
																					details?.data
																						.introductionMediaVideoCover.url
																				}
																				width={390}
																				height={138}
																				style={{
																					objectFit: 'contain',
																				}}
																			/>
																		)
																	}
																/>
															</Grid>
														</>
													)}
												</>
											)}
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
										</Box>
										<MeditationInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>

				<Card sx={{ marginTop: 3 }}>
					<CardContent>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h6'>
								{t('meditation.exerciseList')}
							</Typography>
							<Stack direction={'row'} alignItems={'center'} spacing={3}>
								<SetSingleNewDialog />
								<SetSingleOrderDialog />
								<SetSingleStatusDialog />
								<Button
									variant='contained'
									style={{ alignItems: 'center' }}
									endIcon={<NoteAltOutlinedIcon />}
									onClick={() =>
										navigate(
											`/exercise-manage/meditation-list/info/${meditationId}/add`
										)
									}
								>
									{t('button.add')}
								</Button>
							</Stack>
						</Box>
						<FormProvider {...methods}>
							<Box marginTop={2}>
								<AppDataGrid
									loading={!isGetPropertySummarySuccess}
									rows={transformResponse(propertySummary?.data?.items) ?? []}
									rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.singleId}
								/>
							</Box>
						</FormProvider>
					</CardContent>
				</Card>
			</Box>
		</>
	);
}
